import cx from 'classnames'
import { useState, useEffect, useCallback } from 'react'
import Popup from 'reactjs-popup'

import { tinykeys } from '@lib/tinykeys'
import { type SanityFrameBuilderTextureImage } from '../../data/sanity/queries/types/texture'

import Arrows from './arrows'
import { type ImageSize } from './image-slide'
import ImageSlides from './image-slides'
import Thumbnails from './thumbnails'

interface FrameBuilderGalleryProps {
  images: SanityFrameBuilderTextureImage[]
  className?: string
}

const FrameBuilderGallery = ({
  images,
  className,
}: FrameBuilderGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [currentImageSize, setCurrentImageSize] = useState<ImageSize>({})

  // Reset index when images change
  useEffect(() => {
    setSelectedIndex(0)
  }, [images])

  const showPreviousImage = useCallback(() => {
    setSelectedIndex((index) => {
      return index > 0 ? index - 1 : index
    })
  }, [setSelectedIndex])
  const showNextImage = useCallback(() => {
    setSelectedIndex((index) => {
      return index < images.length - 1 ? index + 1 : index
    })
  }, [images, setSelectedIndex])

  // Subscribe to left and right arrow keypresses
  useEffect(() => {
    let unsubscribe: () => void = () => {}

    if (isModalOpen) {
      unsubscribe = tinykeys(window, {
        ArrowLeft: showPreviousImage,
        ArrowRight: showNextImage,
      })
    }

    return () => {
      unsubscribe()
    }
  }, [isModalOpen, showPreviousImage, showNextImage])

  return (
    <div className={cx('w-full sm:w-[64px]', className)}>
      <Thumbnails
        images={images}
        setIsModalOpen={setIsModalOpen}
        setSelectedIndex={setSelectedIndex}
      />

      <Popup
        modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        lockScroll
        closeOnDocumentClick
        closeOnEscape
        repositionOnResize
        overlayStyle={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          cursor: 'zoom-out',
        }}
        contentStyle={{
          width: '100%',
          height: '100%',
          padding: '0',
          border: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <Arrows
          imageSize={currentImageSize}
          leftDisabled={selectedIndex <= 0}
          rightDisabled={selectedIndex >= images.length - 1}
          onLeftClick={showPreviousImage}
          onRightClick={showNextImage}
        />

        <ImageSlides
          images={images}
          selectedIndex={selectedIndex}
          onSwipedLeft={showNextImage}
          onSwipedRight={showPreviousImage}
          setIsModalOpen={setIsModalOpen}
          setCurrentImageSize={setCurrentImageSize}
        />
      </Popup>
    </div>
  )
}

export default FrameBuilderGallery
